// PDF utilities for the Vitaliteit Benchmark 2026 report.
// Extracted from VitaliteitBenchmarkPage.js to keep the page file lean.

import jsPDF from 'jspdf';
import QRCode from 'qrcode';

const fmtCur = (n) =>
  new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(Math.round(n || 0));
const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;
const fmtNum = (n) =>
  new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 1 }).format(n || 0);

// ─── Helpers (footer + recommendations) ────────────────────────────────────
function drawFooter(doc, W, H, MARGIN_X, reportId, generatedAt) {
  const CONTENT_W = W - MARGIN_X * 2;
  doc.setDrawColor(226, 232, 240);
  doc.line(MARGIN_X, H - 50, W - MARGIN_X, H - 50);
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  const disclaimer = doc.splitTextToSize(
    'Disclaimer: deze berekening is indicatief en uitsluitend bedoeld als richtlijn op basis van openbare TNO/CBS SBI-benchmarks. Aan deze indicatieve berekening en cijfers kunnen geen rechten worden ontleend. Werkelijke kosten en besparingen zijn afhankelijk van veel factoren zoals contractvorm, ziekteduur, vervangingskosten en sector-specifieke omstandigheden. Voor een onderbouwd advies op maat: neem contact op met Zynq.',
    CONTENT_W
  );
  doc.text(disclaimer, MARGIN_X, H - 42);
  doc.setFontSize(7);
  doc.setTextColor(148, 163, 184);
  doc.text(
    `Rapport-ID: ${reportId}  |  Bron: TNO/CBS SBI-benchmarks  |  Zynq Vitaliteit Benchmark 2026  |  Gegenereerd: ${generatedAt.toLocaleString('nl-NL')}`,
    MARGIN_X,
    H - 18
  );
  const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
  doc.text(`Pagina ${pageNum}`, W - MARGIN_X, H - 18, { align: 'right' });
}

function buildRecommendations({ verzuim, budget, sectorBenchmark, potentialSavings }) {
  const recs = [];
  const verzuimWorse = verzuim > sectorBenchmark.verzuim;
  const verzuimMuchWorse = verzuim > sectorBenchmark.verzuim + 1.5;
  const budgetBelow = budget < sectorBenchmark.budget;
  const budgetMuchBelow = budget < sectorBenchmark.budget * 0.6;
  const burnoutHigh = sectorBenchmark.burnout >= 20;

  if (verzuimMuchWorse) {
    recs.push({
      title: 'Verzuim ligt aanzienlijk boven sectorgemiddelde - directe interventie aanbevolen',
      body: `Het verschil van ${(verzuim - sectorBenchmark.verzuim).toFixed(1)} procentpunt vertaalt zich naar circa ${fmtCur(potentialSavings)} per jaar aan vermijdbare verzuimkosten. Start met een verzuimanalyse op afdelingsniveau, een gerichte werkdrukmonitor en vroegsignalering via leidinggevenden.`,
    });
  } else if (verzuimWorse) {
    recs.push({
      title: 'Verzuim ligt licht boven sectorgemiddelde',
      body: 'Onderzoek welke teams of afdelingen het meest bijdragen aan het verzuim. Inzet van preventieve gezondheidschecks en re-integratiebegeleiding levert hier doorgaans de snelste winst.',
    });
  } else {
    recs.push({
      title: 'Verzuim onder sectorgemiddelde - borgen en doorpakken',
      body: "Jullie scoren beter dan de sector. Borg dit door je preventiebeleid te formaliseren, met heldere KPI's en periodieke MTO's. Richt je vervolgens op duurzame inzetbaarheid om dit op lange termijn vast te houden.",
    });
  }

  if (budgetMuchBelow) {
    recs.push({
      title: 'Vitaliteitsbudget per FTE is fors lager dan sector - vergroot je investeringsruimte',
      body: `Tegenover het sectorgemiddelde van ${fmtCur(sectorBenchmark.budget)} investeer je ${fmtCur(sectorBenchmark.budget - budget)} minder per FTE. Een gerichte verhoging in preventie en mentale veerkracht verdient zichzelf doorgaans binnen 12-18 maanden terug.`,
    });
  } else if (budgetBelow) {
    recs.push({
      title: 'Ruimte om vitaliteitsbudget gerichter in te zetten',
      body: 'Verklein de ruis door je budget te focussen op de drie meest impactvolle interventies: leiderschapsontwikkeling, mentale veerkracht en beweegaanbod. Meet effect via een jaarlijkse vitaliteitsscan.',
    });
  } else {
    recs.push({
      title: 'Vitaliteitsbudget op of boven sectorniveau - maximaliseer rendement',
      body: 'Je investeert genoeg. Focus nu op meetbaarheid (zoals werkvermogenscore en eNPS) en interne communicatie zodat medewerkers de aanwezige vitaliteitsregelingen daadwerkelijk gebruiken.',
    });
  }

  if (burnoutHigh) {
    recs.push({
      title: 'Sector heeft verhoogd burn-out risico - werk preventief aan mentale veerkracht',
      body: `In jouw sector ervaart ${sectorBenchmark.burnout}% verhoogde burn-out klachten. Implementeer structurele check-ins, leiderschapscoaching gericht op psychologische veiligheid en faciliteer hersteltijd in de werkweek.`,
    });
  }

  if (potentialSavings >= 25000) {
    recs.push({
      title: 'Hoge potentiele ROI - plan een onderbouwde businesscase',
      body: `De ingeschatte jaarlijkse besparing van ${fmtCur(potentialSavings)} rechtvaardigt een onderbouwde businesscase. Werk samen met Zynq aan een 12-maanden roadmap met meetbare tussenstappen.`,
    });
  }

  return recs.slice(0, 5);
}

// ─── Main exported PDF generator ───────────────────────────────────────────
export async function generateBenchmarkPdf({
  company,
  sector,
  fteNum,
  verzuimNum,
  budgetNum,
  sectorBenchmark,
  extraBenchmarks = [],
  potentialSavings,
  workingDays,
  contactName,
  contactEmail,
  cfg,
  submissionId,
}) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const MARGIN_X = 40;
  const CONTENT_W = W - MARGIN_X * 2;
  const FOOTER_TOP = H - 60;

  const reportId =
    submissionId || `VB-${new Date().getTime().toString(36).toUpperCase()}`;
  const generatedAt = new Date();

  let y = 50;

  const ensureSpace = (needed) => {
    if (y + needed > FOOTER_TOP) {
      drawFooter(doc, W, H, MARGIN_X, reportId, generatedAt);
      doc.addPage();
      y = 60;
    }
  };

  const sectionHeader = (title) => {
    ensureSpace(40);
    doc.setTextColor(30, 41, 59);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(title, MARGIN_X, y);
    y += 8;
    doc.setDrawColor(226, 232, 240);
    doc.line(MARGIN_X, y, W - MARGIN_X, y);
    y += 16;
  };

  // ── Header band ──
  doc.setFillColor(16, 185, 129);
  doc.rect(0, 0, W, 70, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Vitaliteit Benchmark 2026', MARGIN_X, 44);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Zynq - vitaliteit als missie', MARGIN_X, 60);
  doc.setFontSize(8);
  doc.text(`Rapport-ID: ${reportId}`, W - MARGIN_X, 60, { align: 'right' });

  y = 95;
  doc.setTextColor(30, 41, 59);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`Rapport voor ${company}`, MARGIN_X, y);
  y += 16;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text(`Sector: ${sector}`, MARGIN_X, y);
  y += 12;
  if (extraBenchmarks.length > 0) {
    doc.text(
      `Vergeleken met: ${extraBenchmarks.map((b) => b.sector.split(':')[0]).join(', ')}`,
      MARGIN_X,
      y
    );
    y += 12;
  }
  doc.text(
    `Datum: ${generatedAt.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}  |  Contact: ${contactName} <${contactEmail}>`,
    MARGIN_X,
    y
  );
  y += 18;

  // ── Sector kerngetallen strip ──
  const stripCards = [
    { label: 'Sector verzuim', value: fmtPct(sectorBenchmark.verzuim) },
    { label: 'Sector budget/FTE', value: fmtCur(sectorBenchmark.budget) },
    { label: 'Burn-out risico', value: `${sectorBenchmark.burnout}%` },
    { label: 'Verzuimkosten/dag', value: fmtCur(sectorBenchmark.dagKosten) },
  ];
  const cardW = (CONTENT_W - 12) / 4;
  const cardH = 50;
  stripCards.forEach((c, i) => {
    const x = MARGIN_X + i * (cardW + 4);
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(x, y, cardW, cardH, 6, 6, 'FD');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'normal');
    doc.text(c.label, x + 10, y + 17);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(6, 95, 70);
    doc.text(c.value, x + 10, y + 37);
  });
  y += cardH + 18;

  // ── Ingevulde gegevens ──
  sectionHeader('Ingevulde gegevens');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const rows = [
    ['Bedrijfsnaam', company],
    ['Sector', sector],
    ...(extraBenchmarks.length > 0
      ? [['Vergelijkings-sectoren', extraBenchmarks.map((b) => b.sector).join(' | ')]]
      : []),
    ['Aantal FTE', fmtNum(fteNum)],
    ['Huidig verzuim', fmtPct(verzuimNum)],
    ['Vitaliteitsbudget per FTE', fmtCur(budgetNum)],
  ];
  rows.forEach(([k, v]) => {
    ensureSpace(16);
    doc.setTextColor(100, 116, 139);
    doc.text(k, MARGIN_X, y);
    doc.setTextColor(30, 41, 59);
    doc.setFont('helvetica', 'bold');
    const valLines = doc.splitTextToSize(String(v), CONTENT_W - 180);
    doc.text(valLines, MARGIN_X + 180, y);
    doc.setFont('helvetica', 'normal');
    y += 14 + (valLines.length - 1) * 12;
  });
  y += 6;

  // FTE explainer
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  const fteExplain = doc.splitTextToSize(
    'Toelichting FTE: een FTE (Full-Time Equivalent) is het totaal aantal contracturen per week gedeeld door een volledige werkweek (meestal 40 uur). Twee medewerkers van 20 uur = 1 FTE.',
    CONTENT_W
  );
  ensureSpace(fteExplain.length * 11 + 12);
  doc.text(fteExplain, MARGIN_X, y);
  y += fteExplain.length * 11 + 12;

  // ── Visuele balk-vergelijking (A3) ──
  sectionHeader('Visuele vergelijking');
  const drawBar = (label, you, sectorVal, betterLow, unit) => {
    ensureSpace(50);
    const target = betterLow ? sectorVal * 0.75 : sectorVal * 1.3;
    const maxVal = Math.max(you, sectorVal, target) * 1.15 || 1;
    const barX = MARGIN_X + 130;
    const barW = CONTENT_W - 140;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85);
    doc.text(label, MARGIN_X, y + 11);
    // Background
    doc.setFillColor(241, 245, 249);
    doc.roundedRect(barX, y + 4, barW, 14, 3, 3, 'F');
    const drawSeg = (val, color) => {
      const w = Math.max(2, Math.min(barW, (val / maxVal) * barW));
      doc.setFillColor(...color);
      doc.roundedRect(barX, y + 4, w, 14, 3, 3, 'F');
      return w;
    };
    // Top-25% marker line (dashed)
    const targetX = barX + (target / maxVal) * barW;
    doc.setDrawColor(13, 148, 136);
    doc.setLineDashPattern([2, 2], 0);
    doc.line(targetX, y + 1, targetX, y + 22);
    doc.setLineDashPattern([], 0);
    // You bar
    const youColor = betterLow
      ? you <= sectorVal
        ? [16, 185, 129]
        : [239, 68, 68]
      : you >= sectorVal
        ? [16, 185, 129]
        : [245, 158, 11];
    drawSeg(you, youColor);
    // Sector marker line
    const sectorX = barX + (sectorVal / maxVal) * barW;
    doc.setDrawColor(71, 85, 105);
    doc.setLineWidth(1.5);
    doc.line(sectorX, y + 2, sectorX, y + 21);
    doc.setLineWidth(0.4);
    // Labels
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    const valFmt = (v) => (unit === '€' ? fmtCur(v) : `${fmtNum(v)}${unit}`);
    doc.text(`Jouw: ${valFmt(you)}`, barX, y + 32);
    doc.text(`Sector: ${valFmt(sectorVal)}`, barX + 75, y + 32);
    doc.text(`Top-25%: ${valFmt(target)}`, barX + 165, y + 32);
    y += 42;
  };
  drawBar('Verzuim', verzuimNum, sectorBenchmark.verzuim, true, '%');
  drawBar('Budget per FTE', budgetNum, sectorBenchmark.budget, false, '€');
  y += 6;
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  doc.text(
    'Legenda: gevulde balk = jouw bedrijf  |  verticale lijn = sectorgemiddelde  |  gestreepte lijn = top-25% benchmark',
    MARGIN_X,
    y
  );
  y += 16;

  // ── Vergelijkingstabel (multi-sector) ──
  sectionHeader(
    extraBenchmarks.length > 0
      ? 'Vergelijking met sectoren'
      : 'Vergelijking met sectorbenchmark'
  );
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  const allSectors = [
    { sector, ...sectorBenchmark, primary: true },
    ...extraBenchmarks,
  ];
  const headers = ['Bron', 'Verzuim', 'Budget/FTE', 'Burn-out', 'Dagkosten'];
  const colX = [
    MARGIN_X,
    MARGIN_X + 250,
    MARGIN_X + 320,
    MARGIN_X + 390,
    MARGIN_X + 450,
  ];
  headers.forEach((h, i) => doc.text(h, colX[i], y));
  y += 6;
  doc.line(MARGIN_X, y, W - MARGIN_X, y);
  y += 12;
  doc.setFont('helvetica', 'normal');
  ensureSpace(16);
  doc.setTextColor(6, 95, 70);
  doc.setFont('helvetica', 'bold');
  doc.text(`Uw bedrijf (${company.slice(0, 28)})`, colX[0], y);
  doc.text(fmtPct(verzuimNum), colX[1], y);
  doc.text(fmtCur(budgetNum), colX[2], y);
  doc.text('-', colX[3], y);
  doc.text('-', colX[4], y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(30, 41, 59);
  allSectors.forEach((b) => {
    ensureSpace(16);
    const label = b.primary
      ? `${b.sector.split(':')[0]} (primair)`
      : b.sector.split(':')[0];
    doc.text(label, colX[0], y);
    doc.setTextColor(100, 116, 139);
    const fullName = b.sector.split(':')[1]?.trim() || '';
    const fullLines = doc.splitTextToSize(fullName, 200);
    doc.text(fullLines[0] || '', colX[0] + 30, y);
    doc.setTextColor(30, 41, 59);
    doc.text(fmtPct(b.verzuim), colX[1], y);
    doc.text(fmtCur(b.budget), colX[2], y);
    doc.text(`${b.burnout}%`, colX[3], y);
    doc.text(fmtCur(b.dagKosten), colX[4], y);
    y += 14;
  });
  y += 10;

  // ── ROI box ──
  ensureSpace(120);
  const roiLabelTxt = cfg.roi_label || 'Jaarlijkse potentiële besparing';
  const deltaPct = verzuimNum - sectorBenchmark.verzuim;
  const extraSickDays = (deltaPct / 100) * workingDays * fteNum;
  const deltaLabel =
    deltaPct >= 0 ? `+${deltaPct.toFixed(1)}%` : `${deltaPct.toFixed(1)}%`;
  const roiSteps = [
    'Formule: (uw verzuim - sectorverzuim) / 100 x werkdagen/jaar x FTE x verzuimkosten per dag',
    `Stap 1  Verschil verzuim: ${fmtPct(verzuimNum)} - ${fmtPct(sectorBenchmark.verzuim)} = ${deltaLabel}`,
    `Stap 2  Extra ziekdagen: (${deltaLabel} / 100) x ${workingDays} werkdagen x ${fmtNum(fteNum)} FTE = ${fmtNum(extraSickDays)} dagen`,
    `Stap 3  Kosten: ${fmtNum(extraSickDays)} dagen x ${fmtCur(sectorBenchmark.dagKosten)} per dag = ${fmtCur(potentialSavings)}`,
  ];
  const roiBoxPaddingX = 16;
  const wrappedLines = [];
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  roiSteps.forEach((line) => {
    doc.splitTextToSize(line, CONTENT_W - roiBoxPaddingX * 2).forEach((p) => wrappedLines.push(p));
  });
  const linesH = wrappedLines.length * 11;
  const roiBoxH = 18 + 28 + 6 + linesH + 16 + 6;
  ensureSpace(roiBoxH + 12);
  doc.setFillColor(236, 253, 245);
  doc.setDrawColor(16, 185, 129);
  doc.roundedRect(MARGIN_X, y, CONTENT_W, roiBoxH, 8, 8, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(6, 95, 70);
  doc.text(roiLabelTxt, MARGIN_X + roiBoxPaddingX, y + 22);
  doc.setFontSize(22);
  doc.text(fmtCur(potentialSavings), MARGIN_X + roiBoxPaddingX, y + 52);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(51, 65, 85);
  let lineY = y + 70;
  wrappedLines.forEach((line) => {
    doc.text(line, MARGIN_X + roiBoxPaddingX, lineY);
    lineY += 11;
  });
  y += roiBoxH + 14;

  // ── Meerjarige prognose (A2) ──
  sectionHeader('Meerjarige besparingsprognose');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(
    'Bij gelijkblijvend verschil met sectorgemiddelde, exclusief inflatie of CAO-correctie.',
    MARGIN_X,
    y
  );
  y += 14;
  const forecast = [
    { period: 'Jaar 1', cum: potentialSavings * 1 },
    { period: 'Jaar 3 (cumulatief)', cum: potentialSavings * 3 },
    { period: 'Jaar 5 (cumulatief)', cum: potentialSavings * 5 },
  ];
  const fcCardW = (CONTENT_W - 12) / 3;
  forecast.forEach((f, i) => {
    const x = MARGIN_X + i * (fcCardW + 6);
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(16, 185, 129);
    doc.roundedRect(x, y, fcCardW, 56, 6, 6, 'FD');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(f.period, x + 10, y + 17);
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(6, 95, 70);
    doc.text(fmtCur(f.cum), x + 10, y + 40);
  });
  y += 56 + 18;

  // ── Aanbevelingen (A1) ──
  sectionHeader('Aanbevelingen');
  const recommendations = buildRecommendations({
    verzuim: verzuimNum,
    budget: budgetNum,
    sectorBenchmark,
    potentialSavings,
  });
  recommendations.forEach((r) => {
    const txt = doc.splitTextToSize(r.title, CONTENT_W - 24);
    const body = doc.splitTextToSize(r.body, CONTENT_W - 24);
    const blockH = 6 + txt.length * 12 + body.length * 11 + 14;
    ensureSpace(blockH);
    doc.setFillColor(16, 185, 129);
    doc.circle(MARGIN_X + 5, y + 6, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(30, 41, 59);
    doc.text(txt, MARGIN_X + 18, y + 10);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text(body, MARGIN_X + 18, y + 10 + txt.length * 12);
    y += blockH;
  });
  y += 6;

  // ── Burnout context ──
  ensureSpace(60);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(30, 41, 59);
  doc.text('Burn-out risico in uw sector', MARGIN_X, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  const burnText = doc.splitTextToSize(
    `In de sector "${sector}" heeft gemiddeld ${sectorBenchmark.burnout}% van de werknemers verhoogde burn-out klachten. Dit is een belangrijke context bij uw vitaliteitsstrategie.`,
    CONTENT_W
  );
  doc.text(burnText, MARGIN_X, y);
  y += burnText.length * 12 + 10;

  // ── QR (A5) ──
  try {
    const baseBookingUrl = (cfg?.booking_url || '').trim() || 'https://zynq.org/boeken/';
    // Voeg een ref-parameter toe zodat we conversies kunnen attribueren
    const sep = baseBookingUrl.includes('?') ? '&' : '?';
    const ctaUrl = `${baseBookingUrl}${sep}ref=${encodeURIComponent(reportId)}&tool=vitaliteit_benchmark`;
    const qrDataUrl = await QRCode.toDataURL(ctaUrl, {
      width: 200,
      margin: 0,
      color: { dark: '#065f46', light: '#ffffff' },
    });
    const qrSize = 70;
    ensureSpace(qrSize + 16);
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(MARGIN_X, y, CONTENT_W, qrSize + 10, 6, 6, 'FD');
    doc.addImage(qrDataUrl, 'PNG', MARGIN_X + 8, y + 5, qrSize, qrSize);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(6, 95, 70);
    doc.text('Klaar voor de volgende stap?', MARGIN_X + qrSize + 24, y + 22);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    const ctaLines = doc.splitTextToSize(
      'Scan de QR-code voor een vrijblijvend vervolggesprek en een onderbouwd advies op maat. Of bezoek bovenstaande URL met rapport-referentie.',
      CONTENT_W - qrSize - 40
    );
    doc.text(ctaLines, MARGIN_X + qrSize + 24, y + 38);
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text(ctaUrl, MARGIN_X + qrSize + 24, y + qrSize + 2);
    y += qrSize + 16;
  } catch {
    // QR optional
  }

  // Footer on final page
  drawFooter(doc, W, H, MARGIN_X, reportId, generatedAt);

  const safeName = company.replace(/[^a-z0-9-_]/gi, '_').slice(0, 40) || 'bedrijf';
  doc.save(`Vitaliteit_Benchmark_2026_${safeName}.pdf`);
}
